/** Progress snapshot emitted after each device is attempted. */
export interface BulkAddProgress {
  total: number;
  current: number;
  succeeded: number;
  failed: number;
  errors: Array<{ deviceName: string; error: string }>;
  isComplete: boolean;
}

export interface BulkAddResult {
  succeeded: number;
  failed: number;
  errors: Array<{ deviceName: string; error: string }>;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AddDeviceFn = (device: Record<string, any>) => Promise<unknown>;

/**
 * Add an array of devices sequentially, calling `onProgress` after each attempt.
 *
 * Designed for USGS bulk add: the caller supplies the RTK Query `addConnectedDevice`
 * trigger (wrapped in a thin function that calls `.unwrap()`) and a state-setter
 * callback to drive a progress bar.
 *
 * @param devices    Array of formatted device payloads ready to POST.
 * @param addFn      Async function that posts a single device; should throw on error.
 * @param onProgress Called after every attempt with a fresh progress snapshot.
 * @returns Final succeeded/failed counts and any per-device error details.
 */
export async function bulkAddDevices(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  devices: Record<string, any>[],
  addFn: AddDeviceFn,
  onProgress: (progress: BulkAddProgress) => void,
): Promise<BulkAddResult> {
  const errors: BulkAddProgress['errors'] = [];
  let succeeded = 0;
  let failed = 0;

  for (let i = 0; i < devices.length; i++) {
    const device = devices[i];
    const deviceName: string =
      (device.alias as string) || (device.name as string) || `Device ${i + 1}`;

    try {
      await addFn(device);
      succeeded++;
    } catch (err) {
      failed++;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const errorMessage: string =
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (err as any)?.data?.message ??
        (err as Error)?.message ??
        'Unknown error';
      errors.push({ deviceName, error: errorMessage });
    }

    onProgress({
      total: devices.length,
      current: i + 1,
      succeeded,
      failed,
      errors,
      isComplete: i === devices.length - 1,
    });
  }

  return { succeeded, failed, errors };
}
